import {createTheme} from '@mui/material'
import SideBarLogoDefaultClose from 'src/images/logos/brain-default/SideBarLogoClose.svg'
import SideBarLogoDefaultOpen from 'src/images/logos/brain-default/SideBarLogoOpen.svg'
import SideBarLogoSekronClose from 'src/images/logos/sekron/SideBarLogoClose.svg'
import SideBarLogoSekronOpen from 'src/images/logos/sekron/SideBarLogoOpen.svg'

import LoginPageLogoDefault from 'src/images/logos/brain-default/LoginPagesLogo.svg'
import LoginPageLogoSekron from 'src/images/logos/sekron/LoginPagesLogo.svg'

import {DefaultTheme} from 'styled-components'

enum CompaniesListWithPersonalizedDashboards {
  SEKRON = 'sekron',
  LOCAL = 'localhost:8000',
}

export enum CompaniesIdsOnSekronThemeWithDashboardDefaultEnum {
  SWIFT = 139,
}

export enum CompaniesIdsOnCloudThemeWithDashboardPersonalizedEnum {
  CERQUILHO = 198,
  DEMO = 116,
}

export const CompaniesWithPersonalizedDashboards = [CompaniesListWithPersonalizedDashboards.SEKRON]

function usePersonalizedThemeByCompany() {
  const styledThemeDefault: DefaultTheme = {
    '*': {
      fontFamily: 'Inter, sans-serif',
    },
    skeleton: {
      backgroundColor: '#e9e8e9',
    },
    fonts: {
      primary: 'Inter, sans-serif',
    },
    colors: {
      text: '#55565B',
      primary: '#258be3',
      secondary: '#364152',
      required: '#FF0000',
      placeholder: '#B9B9B9',
      focus: '#258BE3',
      success: '#4A934A',
      error: '#F04438',
      disabled: '#B9B9B9',
      disabledBackground: '#F5F5F5',
      white: '#FFFFFF',
      black: '#000000',
    },
    hoverColors: {
      primary: '#0F4D80',
      secondary: '#258BE31F',
      error: '#B42318',
      success: '#376E37',
    },
    input: {
      height: '40px',
      fontSize: '14px',
      fontWeight: 500,
      borderRadius: '8px',
      labelFontSize: '14px',
      labelFontWeight: 500,
      labelFontFamily: 'Inter, sans-serif',
      borderColor: '#8E8E8E',
      paddingVertical: '8px',
      paddingHorizontal: '14px',
      fontFamily: 'Inter, sans-serif',
      labelColor: '#364152',
      backgroundColor: '#FFFFFF',
    },
    colorPicker: {
      backgroundColor: '#FFFFFF',
      borderRadius: '8px',
      boxShadow: '1px 1px 1px 1px #000000',
      colorsBoxShadow: ' 1px 2px 5px #000000',
      colorsWidth: '1.35rem',
      colorsHeight: '1.35rem',
      colorsBorderRadius: '3px',
    },
    calendar: {
      color: '#344054',
      fontSize: '14px',
      fontWeight: 500,
      hoverColor: '#000',
      disabledColor: '#B9B9B9',
      headerFontWeight: 700,
      headerFontSize: '16px',
      backgroundColor: 'transparent',
      selectedDayColor: '#fff',
      actualDateColor: '#009EFF',
      actualDateBorder: 'transparent',
      navigationArrowsColor: '#fff',
      notActualMonthColor: 'gray',
      fontFamily: 'Inter, sans-serif',
      disabledBackgroundColor: 'transparent',
      actualDateBackgroundColor: 'transparent',
      hoverBackgroundColor: '#E7EFFF',
      notActualMonthBackgroundColor: 'transparent',
      selectedDayBackgroundColor: '#009EFF',
      headerFontFamily: 'Inter, sans-serif',
      navigationArrowsBackgroundColor: '#fff',
      paddingBottom: '1rem',
      paddingTop: '1rem',
      paddingLeft: '1rem',
      paddingRight: '1rem',
      padding: '1rem',
    },
    checkbox: {
      size: '18px',
      gap: '8px',
      borderRadius: '4px',
      marginVertical: '8px',
      labelColor: '#364152',
    },
    button: {
      fontSize: '16px',
      boxShadow: 'none',
      borderRadius: '8px',
    },
    dropdown: {
      fontSize: '14px',
      fontWeight: 500,
      scrollbarWidth: '12px',
      paddingVertical: '8px',
      itemHoverColor: '#f5f5f5',
      paddingHorizontal: '14px',
      backgroundColor: '#FFFFFF',
      scrollbarBackground: '#8E8E8E',
    },
    modal: {
      borderRadius: '8px',
      backgroundColor: '#8E8E8E',
      boxShadow: '1px 1px 1px 1px rgba(0,0,0,0.5)',
      paddingTop: '0px',
      paddingBottom: '0px',
      paddingLeft: '0px',
      paddingRight: '0px',
      marginBottom: '0px',
      marginTop: '0px',
      marginLeft: '0px',
      marginRight: '0px',
      borderColor: '#8E8E8E',
      padding: '0px',
      margin: '0px',
      border: '1px solid #8E8E8E',
      bgBackgroundColor: 'rgba(0,0,0,0.7)',
    },
    snackbar: {
      errorStyle: {
        color: '#000',
        iconColor: '#FF6363',
        backgroundColor: '#fff',
      },
      warningStyle: {
        color: '#000',
        iconColor: '#ff9800',
        backgroundColor: '#fff',
      },
      successStyle: {
        color: '#000',
        iconColor: '#54B45C',
        backgroundColor: '#fff',
      },
      infoStyle: {
        color: '#000',
        iconColor: '#2196f3',
        backgroundColor: '#fff',
      },
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
      height: '4rem',
    },
    table: {
      tableHeader: {
        padding: '0px',
        fontWeight: 400,
        color: '#364152',
        fontSize: '14px',
        height: '3rem',
        textAlign: 'start',
        borderColor: '#6D7392',
        backgroundColor: 'transparent',
        fontFamily: 'Inter, sans-serif',
        borderBottom: '2px solid #6D7392',
      },
      tableBody: {
        padding: '5px',
        fontWeight: 300,
        color: '#000',
        fontSize: '13px',
        textAlign: 'start',
        rowHeight: '2.5rem',
        borderColor: '#B9B9B9',
        backgroundColor: 'transparent',
        fontFamily: 'Inter, sans-serif',
        borderBottom: '1px solid #B9B9B9',
      },
      tableAdvancedFilter: {
        backgroundColor: 'transparent',
        borderColor: '#F5F5F5',
        width: '95%',
        height: '2.5rem',
      },
    },
    textArea: {
      height: '40px',
      fontSize: '14px',
      fontWeight: 500,
      borderRadius: '8px',
      borderColor: '#8E8E8E',
      paddingVertical: '8px',
      paddingHorizontal: '14px',
      fontFamily: 'Inter, sans-serif',
      backgroundColor: '#FFFFFF',
    },
    stepper: {
      concludedColor: '#009D10',
      activedColor: '#009EFF',
      pendingColor: '#D6E2F3',
    },
    toggle: {
      backgroundColor: '#B9B9B9',
      disabledKnob: '#F5F5F5',
    },
    tr: {
      borderBottom: '1px solid #B9B9B9',
    },
    accordion: {
      hoverBackgroundColor: '#F5F5F5',
    },
    tooltip: {
      backgroundColor: '#364152',
      borderRadius: '8px',
      width: 'auto',
      height: 'auto',
      margin: '0px',
      padding: '2px 10px',
      fontSize: '14px',
      color: '#FFFFFF',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      position: 'top',
      marginTop: '0px',
      marginBottom: '0px',
      marginLeft: '0px',
      marginRight: '0px',
      paddingTop: '0px',
      paddingBottom: '0px',
      paddingLeft: '0px',
      paddingRight: '0px',
      border: '1px solid #364152',
      opacity: '1',
      left: '0px',
      top: '0px',
      pointerEvents: 'none',
      transform: 'none',
    },
    icons: {
      colors: {
        darkContour: '#0F4C81',
        lightContour: '#87C7FF',
      },
      trendingIcon: {
        upBackgroungColor: '#D1FADF',
        upTextColor: '#027A48',
        downBackgroundColor: '#FECDCA',
        downTextColor: '#B42318',
      },
    },
    dashboard: {
      colors: {
        title: '#0F4C81',
      },
      heatMapColorRanges: {
        lowest: '#7CD4FD',
        lower: '#36BFFA',
        low: '#0BA5EC',
        lowMedium: '#FEB273',
        lowerMedium: '#FD853A',
        medium: '#FB6514',
        high: '#EC4A0A',
        extreme: '#D92D20',
      },
    },
    sidebar: {
      backgroundColor: '#1d1e28',
      backgroundColorSeleted: '#46464f',
      paddingOnIcon: '20px',
    },
    advancedFilter: {
      placeholderColor: '#1976d2',
      iconColor: '#1976d2',
      backgroundColor: 'rgba(0, 158, 255, 0.05)',
      borderColor: '#009eff',
    },
    login: {
      boxShadowColor: '#0c0d1140',
      backdropColor: '  #00305e33',
      logoWidth: '200px',
    },
    faceRecognition: {
      titleColor: '#0F4C81',
    },
    card: {
      borderColor: '#009dff73',
    },
  }

  const styledThemeSekron: DefaultTheme = {
    '*': {
      fontFamily: 'Inter, sans-serif',
    },
    skeleton: {
      backgroundColor: '#e9e8e9',
    },
    fonts: {
      primary: 'Inter, sans-serif',
    },
    colors: {
      text: '#55565B',
      primary: '#E4212D',
      secondary: '#FECDCA',
      required: '#FF0000',
      placeholder: '#B9B9B9',
      focus: '#E4212D',
      success: '#4A934A',
      error: '#F04438',
      disabled: '#B9B9B9',
      disabledBackground: '#F5F5F5',
      white: '#FFFFFF',
      black: '#000000',
    },
    hoverColors: {
      primary: ' #B42318',
      secondary: '#FECDCA',
      error: '#B42318',
      success: '#376E37',
    },
    input: {
      height: '40px',
      fontSize: '14px',
      fontWeight: 500,
      borderRadius: '8px',
      labelFontSize: '14px',
      labelFontWeight: 500,
      labelFontFamily: 'Inter, sans-serif',
      borderColor: '#8E8E8E',
      paddingVertical: '8px',
      paddingHorizontal: '14px',
      fontFamily: 'Inter, sans-serif',
      labelColor: '#364152',
      backgroundColor: '#FFFFFF',
    },
    colorPicker: {
      backgroundColor: '#FFFFFF',
      borderRadius: '8px',
      boxShadow: '1px 1px 1px 1px #000000',
      colorsBoxShadow: ' 1px 2px 5px #000000',
      colorsWidth: '1.35rem',
      colorsHeight: '1.35rem',
      colorsBorderRadius: '3px',
    },
    calendar: {
      color: '#344054',
      fontSize: '14px',
      fontWeight: 500,
      hoverColor: '#000',
      disabledColor: '#B9B9B9',
      headerFontWeight: 700,
      headerFontSize: '16px',
      backgroundColor: 'transparent',
      selectedDayColor: '#fff',
      actualDateColor: '#E4212D',
      actualDateBorder: 'transparent',
      navigationArrowsColor: '#fff',
      notActualMonthColor: 'gray',
      fontFamily: 'Inter, sans-serif',
      disabledBackgroundColor: 'transparent',
      actualDateBackgroundColor: 'transparent',
      hoverBackgroundColor: '#E7EFFF',
      notActualMonthBackgroundColor: 'transparent',
      selectedDayBackgroundColor: '#E4212D',
      headerFontFamily: 'Inter, sans-serif',
      navigationArrowsBackgroundColor: '#fff',
      paddingBottom: '1rem',
      paddingTop: '1rem',
      paddingLeft: '1rem',
      paddingRight: '1rem',
      padding: '1rem',
    },
    checkbox: {
      size: '18px',
      gap: '8px',
      borderRadius: '4px',
      marginVertical: '8px',
      labelColor: '#364152',
    },
    button: {
      fontSize: '16px',
      boxShadow: 'none',
      borderRadius: '8px',
    },
    dropdown: {
      fontSize: '14px',
      fontWeight: 500,
      scrollbarWidth: '12px',
      paddingVertical: '8px',
      itemHoverColor: '#f5f5f5',
      paddingHorizontal: '14px',
      backgroundColor: '#FFFFFF',
      scrollbarBackground: '#8E8E8E',
    },
    modal: {
      borderRadius: '8px',
      backgroundColor: '#8E8E8E',
      boxShadow: '1px 1px 1px 1px rgba(0,0,0,0.5)',
      paddingTop: '0px',
      paddingBottom: '0px',
      paddingLeft: '0px',
      paddingRight: '0px',
      marginBottom: '0px',
      marginTop: '0px',
      marginLeft: '0px',
      marginRight: '0px',
      borderColor: '#8E8E8E',
      padding: '0px',
      margin: '0px',
      border: '1px solid #8E8E8E',
      bgBackgroundColor: 'rgba(0,0,0,0.7)',
    },
    snackbar: {
      errorStyle: {
        color: '#000',
        iconColor: '#FF6363',
        backgroundColor: '#fff',
      },
      warningStyle: {
        color: '#000',
        iconColor: '#ff9800',
        backgroundColor: '#fff',
      },
      successStyle: {
        color: '#000',
        iconColor: '#54B45C',
        backgroundColor: '#fff',
      },
      infoStyle: {
        color: '#000',
        iconColor: '#2196f3',
        backgroundColor: '#fff',
      },
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
      height: '4rem',
    },
    table: {
      tableHeader: {
        padding: '0px',
        fontWeight: 400,
        color: '#364152',
        fontSize: '14px',
        height: '3rem',
        textAlign: 'start',
        borderColor: '#6D7392',
        backgroundColor: '#FFFFFF',
        fontFamily: 'Inter, sans-serif',
        borderBottom: '2px solid #6D7392',
      },
      tableBody: {
        padding: '0px',
        fontWeight: 300,
        color: '#000',
        fontSize: '13px',
        textAlign: 'start',
        rowHeight: '2.5rem',
        borderColor: '#B9B9B9',
        backgroundColor: 'inherit',
        fontFamily: 'Inter, sans-serif',
        borderBottom: '1px solid #B9B9B9',
      },
      tableAdvancedFilter: {
        backgroundColor: '#F5F5F5',
        borderColor: '#F5F5F5',
        width: '95%',
        height: '2.5rem',
      },
    },
    textArea: {
      height: '40px',
      fontSize: '14px',
      fontWeight: 500,
      borderRadius: '8px',
      borderColor: '#8E8E8E',
      paddingVertical: '8px',
      paddingHorizontal: '14px',
      fontFamily: 'Inter, sans-serif',
      backgroundColor: '#FFFFFF',
    },
    stepper: {
      concludedColor: '#009D10',
      activedColor: '#B42318',
      pendingColor: '#D6E2F3',
    },
    toggle: {
      backgroundColor: '#B9B9B9',
      disabledKnob: '#F5F5F5',
    },
    tr: {
      borderBottom: '1px solid #B9B9B9',
    },
    accordion: {
      hoverBackgroundColor: '#F5F5F5',
    },
    tooltip: {
      backgroundColor: '#364152',
      borderRadius: '8px',
      width: 'auto',
      height: 'auto',
      margin: '0px',
      padding: '2px 10px',
      fontSize: '14px',
      color: '#FFFFFF',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      position: 'top',
      marginTop: '0px',
      marginBottom: '0px',
      marginLeft: '0px',
      marginRight: '0px',
      paddingTop: '0px',
      paddingBottom: '0px',
      paddingLeft: '0px',
      paddingRight: '0px',
      border: '1px solid #364152',
      opacity: '1',
      left: '0px',
      top: '0px',
      pointerEvents: 'none',
      transform: 'none',
    },
    icons: {
      colors: {
        darkContour: '#E4212D',
        lightContour: '#FF99A0',
      },
      trendingIcon: {
        upBackgroungColor: '#D1FADF',
        upTextColor: '#027A48',
        downBackgroundColor: '#FECDCA',
        downTextColor: '#B42318',
      },
    },
    dashboard: {
      colors: {
        title: '#55565B',
      },
      heatMapColorRanges: {
        lowest: '#7CD4FD',
        lower: '#36BFFA',
        low: '#0BA5EC',
        lowMedium: '#FEB273',
        lowerMedium: '#FD853A',
        medium: '#FB6514',
        high: '#EC4A0A',
        extreme: '#D92D20',
      },
    },
    sidebar: {
      backgroundColor: '#55565B',
      backgroundColorSeleted: '#3E3E41',
      paddingOnIcon: '20px 18px 18px 12px',
    },
    advancedFilter: {
      placeholderColor: '#E4212D',
      iconColor: '#E4212D',
      backgroundColor: 'rgba(254, 205, 202, 0.62)',
      borderColor: '#E4212D',
    },
    login: {
      backdropColor: '#393B3D33',
      boxShadowColor: '#0C0D1140',
      logoWidth: '',
    },
    faceRecognition: {
      titleColor: ' #B42318',
    },
    card: {
      borderColor: '#e4212e69',
    },
  }

  const muiThemeDefault = createTheme({
    palette: {
      primary: {
        main: '#258be3',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          contained: {
            color: 'white',
          },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          text: {
            fill: 'white',
          },
        },
      },
    },
  })

  const muiThemeSekron = createTheme({
    palette: {
      primary: {
        main: '#E4212D',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          contained: {
            color: 'white',
          },
        },
      },
      MuiStepIcon: {
        styleOverrides: {
          text: {
            fill: 'white',
          },
        },
      },
    },
  })

  const url = window.location.href
  const styledTheme = CompaniesWithPersonalizedDashboards.some((companyUrl) => url.includes(companyUrl)) ? styledThemeSekron : styledThemeDefault
  const muiTheme = CompaniesWithPersonalizedDashboards.some((companyUrl) => url.includes(companyUrl)) ? muiThemeSekron : muiThemeDefault

  const sideBarOpenLogo = CompaniesWithPersonalizedDashboards.some((companyUrl) => url.includes(companyUrl)) ? SideBarLogoSekronOpen : SideBarLogoDefaultOpen
  const sideBarCloseLogo = CompaniesWithPersonalizedDashboards.some((companyUrl) => url.includes(companyUrl)) ? SideBarLogoSekronClose : SideBarLogoDefaultClose

  const loginPagesLogo = CompaniesWithPersonalizedDashboards.some((companyUrl) => url.includes(companyUrl)) ? LoginPageLogoSekron : LoginPageLogoDefault
  return {styledTheme, muiTheme, sideBarOpenLogo, sideBarCloseLogo, loginPagesLogo}
}

export default usePersonalizedThemeByCompany
